import { useCallback, useEffect, useMemo, useState } from "react";
import { Download, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/site";
import { WaIcon } from "@/components/landing/WaIcon";
import { auth, fmtDate } from "./auth";

const STATUS = { nuevo: "Nuevo", contactado: "Contactado", cotizacion: "Cotización enviada", cerrado: "Cerrado" };

const exportCsv = (rows) => {
  const head = ["Fecha", "Nombre", "Teléfono", "Correo", "Previsión", "Tipo de plan", "Región", "Estado"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = rows.map((l) => [fmtDate(l.created_at), l.nombre, l.telefono, l.email, l.prevision, l.tipo_plan, l.region, STATUS[l.status]].map(esc).join(";"));
  const blob = new Blob(["\uFEFF" + [head.map(esc).join(";"), ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `contactos-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
};

const Row = ({ l, onStatus, onDelete }) => (
  <div className="grid gap-4 rounded-2xl border border-[#E3EAEF] bg-white p-5 md:grid-cols-[1.3fr_1.2fr_1fr_auto] md:items-center" data-testid={`lead-row-${l.id}`}>
    <div>
      <div className="text-[18px] font-bold text-[#0B3C5D]">{l.nombre}</div>
      <div className="text-[15px] text-[#4A5864]">{l.telefono}{l.email && <> · <a href={`mailto:${l.email}`} className="underline">{l.email}</a></>}</div>
      <div className="text-[14px] text-[#4A5864]">{fmtDate(l.created_at)}</div>
    </div>
    <div className="text-[15px] text-[#1F2A33]">{l.prevision} · Plan {l.tipo_plan?.toLowerCase()}<br /><span className="text-[#4A5864]">Región {l.region}</span></div>
    <select className="field !min-h-[44px] !text-[15px]" value={l.status} onChange={(e) => onStatus(l.id, e.target.value)} data-testid={`lead-status-${l.id}`} aria-label="Estado">
      {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
    </select>
    <div className="flex gap-2">
      <a href={`https://wa.me/${l.telefono.replace(/\D/g, "")}?text=${encodeURIComponent(`Hola ${l.nombre.split(" ")[0]}, te escribo de Plan Isapre Contigo por tu consulta.`)}`}
        target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" data-testid={`lead-wa-${l.id}`}
        className="flex h-11 w-11 items-center justify-center rounded-full bg-[#25D366] text-[#0F2321]"><WaIcon className="h-5 w-5" /></a>
      <button onClick={() => onDelete(l.id)} aria-label="Eliminar" data-testid={`lead-delete-${l.id}`}
        className="flex h-11 w-11 items-center justify-center rounded-full border border-[#E3EAEF] hover:bg-red-50"><Trash2 className="h-5 w-5" /></button>
    </div>
  </div>
);

export const LeadsTab = ({ onLogout }) => {
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("todos");

  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/admin/leads", auth());
      setLeads(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error("No se pudieron cargar los contactos.");
    }
  }, [onLogout]);
  useEffect(() => { load(); }, [load]);

  const onStatus = async (id, status) => {
    await http.patch(`/admin/leads/${id}`, { status }, auth());
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
  };
  const onDelete = async (id) => {
    if (!window.confirm("¿Eliminar este contacto?")) return;
    await http.delete(`/admin/leads/${id}`, auth());
    setLeads((ls) => ls.filter((l) => l.id !== id));
  };

  const shown = useMemo(() => leads.filter((l) => (filter === "todos" || l.status === filter) &&
    `${l.nombre} ${l.telefono} ${l.prevision} ${l.region}`.toLowerCase().includes(q.toLowerCase())), [leads, q, filter]);

  return (
    <div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        {[["todos", "Total"], ...Object.entries(STATUS)].map(([k, v]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`admin-filter-${k}`}
            className={`rounded-2xl p-5 text-left ${filter === k ? "bg-[#0B3C5D] text-white" : "border border-[#E3EAEF] bg-white text-[#0B3C5D]"}`}>
            <div className="text-[32px] font-extrabold leading-none">{k === "todos" ? leads.length : leads.filter((l) => l.status === k).length}</div>
            <div className="mt-2 text-[15px] font-semibold">{v}</div>
          </button>
        ))}
      </div>
      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#4A5864]" />
          <input className="field !pl-12" placeholder="Buscar por nombre, teléfono, región…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-search-input" />
        </div>
        <button onClick={() => exportCsv(shown)} disabled={!shown.length} data-testid="admin-export-csv"
          className="inline-flex min-h-[56px] items-center justify-center gap-2 rounded-xl bg-[#1FA89A] px-6 text-[17px] font-bold text-[#052438] disabled:opacity-50">
          <Download className="h-5 w-5" /> Exportar CSV
        </button>
      </div>
      <div className="mt-6 space-y-3" data-testid="admin-leads-list">
        {shown.length ? shown.map((l) => <Row key={l.id} l={l} onStatus={onStatus} onDelete={onDelete} />)
          : <p className="py-16 text-center text-[18px] text-[#4A5864]" data-testid="admin-empty">Aún no hay contactos aquí.</p>}
      </div>
    </div>
  );
};
